import React, { useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { makeStyles } from '@mui/styles'

import lang from '@/lang'
import IconZoomMinus from '@assets/images/icons/zoom_minus.svg'
import IconZoomPlus from '@assets/images/icons/zoom_plus.svg'
import IconDownload from '@assets/images/icons/download.svg'
import ReloadIconSvg from '@assets/images/icons/reload.svg'
import Panel from '@/components/layouts/panel/index'
import PanelHeader from '@/components/layouts/panel/panel-header'
import { DocumentControlButton, SquareIconLink } from '@/modules/base/components/buttons/index'
import PanelContent from '@/components/layouts/panel/panel-content'
import { CssHelper } from '@/modules/base/view/mixin/helpers_mixin'
import clsx from 'clsx'
import ReceiptPreviewerItem from '@/modules/receipt/preview/item/receipt-previewer-item'
import SignaturePager from '@/modules/signature/components/pager/signature-pager'

const GLOBAL_ROTATE_DEFAULT = 0
const HEADER_HEIGHT = 86

const useStyles = makeStyles(theme => ({
  fileContent: {
    padding: 0,
    backgroundColor: theme.white,
    '& > :first-child': {
      '& > div:first-child': {
        height: HEADER_HEIGHT,
        borderBottom: theme.border,
        borderTop: theme.border,
        borderRadius: 0,
        width: CssHelper.percent(100),
        alignItems: 'center',
      },
    },
  },
  previewItem: {
    maxHeight: `calc(90vh - ${HEADER_HEIGHT}px)`,
  },
  header: {
    height: 86,
    padding: '0px 30px',
    borderBottom: theme.border,
    flexShrink: 0,
    backgroundColor: theme.backgroundColor,
  },
  previewHeader: {
    display: 'flex',
    justifyContent: 'center',
    width: '100%',
    backgroundColor: theme.white,
    '& > *': {
      margin: '20px 0px 20px 20px',
    },
    '& a': {
      border: theme.border,
      backgroundColor: theme.white,
      minWidth: 45,
      minHeight: 45,
    },
    '& a:hover': {
      backgroundColor: theme.backgroundColor,
    },
  },
  button: {
    height: 40,
    maxHeight: 40,
    width: 40,
    maxWidth: 40,
    transition: '.2s',
  },
}))

const DocumentDetailPreview = (props) => {
  const classes = useStyles(props)
  const {
    onZoom,
    zoom,
    file,
    classNames,
    disableActions,
    downloadLabel,
    downloadUrl,
    metaData,
    hideControls,
    withSignatures,
    onOcr,
    onRotate,
    disableOcr,
    onClose,
    disableDownload,
  } = props
  const ROTATE_DEFAULT = useMemo(() => metaData && metaData.rotate ? metaData.rotate * 1 : GLOBAL_ROTATE_DEFAULT, [])
  const [rotate, setRotate] = useState(ROTATE_DEFAULT)

  const rotateRight = () => {
    const degree = (rotate >= 270) ? 0 : rotate + 90
    setRotate(degree)
  }
  const isNotDataLink = !downloadUrl?.startsWith('data:')
  return (
    <div className={clsx([classNames])}>
      {Boolean(withSignatures) && (
        <div className={classes.header}>
          <SignaturePager />
        </div>
      )}

      <Panel>
        {!hideControls && (
          <PanelHeader toolbarRender={() => (
            <div className={classes.previewHeader}>
              <DocumentControlButton
                icon={IconZoomMinus}
                title={lang.expense.previewToolbar.items.zoomOut}
                customClass={classes.button}
                onClick={() => onZoom(-0.1)} />
              <DocumentControlButton
                icon={IconZoomPlus}
                title={lang.expense.previewToolbar.items.zoomIn}
                customClass={classes.button}
                onClick={() => onZoom(+0.1)} />
              <DocumentControlButton
                title={lang.expense.previewToolbar.items.rotate90}
                icon={ReloadIconSvg}
                customClass={classes.documentButton}
                onClick={rotateRight} />
              {!disableDownload && isNotDataLink
                ? (<SquareIconLink
                  customClass={classes.button}
                  icon={IconDownload}
                  tooltip={downloadLabel}
                  href={downloadUrl}
                  target='_blank' />)
                : null}
            </div>
          )} />
        )}
        <PanelContent>
          <div className={classes.fileContent}>
            <ReceiptPreviewerItem
              hideRemove
              downloadUrl={downloadUrl}
              downloadLabel={downloadLabel}
              isNotDataLink={isNotDataLink}
              disableActions={disableActions}
              disableOcr={disableOcr}
              disableDownload={disableDownload}
              onOcr={onOcr}
              onClose={onClose}
              onRotate={onRotate}
              withSignatures={withSignatures}
              isFile
              item={file}
              scale={zoom}
              rotate={rotate}
              size={{}}
              excludeFieldOptions={['description', 'priceTotal']}
              styles={{ previewItem: classes.previewItem }}
            />
          </div>
        </PanelContent>
      </Panel>
    </div>
  )
}

DocumentDetailPreview.propTypes = {
  file: PropTypes.object.isRequired,
  zoom: PropTypes.number,
  classNames: PropTypes.string,
  onZoom: PropTypes.func.isRequired,
  onOcr: PropTypes.func,
  onRotate: PropTypes.func,
  disableActions: PropTypes.bool,
  hideControls: PropTypes.bool,
  onClose: PropTypes.func,
  disableOcr: PropTypes.bool,
  disableDownload: PropTypes.bool,
  downloadLabel: PropTypes.string,
  downloadUrl: PropTypes.string,
  panelClass: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
}

DocumentDetailPreview.defaultProps = {
  zoom: 1,
  hideControls: false,
  withSignatures: false,
  onRotate: () => {},
}

export default DocumentDetailPreview
